import React, { Fragment, useEffect, useState } from 'react';
import { Col, Card, Table, Input, Label, CardHeader, Row } from 'reactstrap';
import Pagination from '../../Components/Pagination/Pagination';
import Empty from '../../Components/Empty/Index';
import { service } from '../../shared/_services/api_service';
import ScorecardModal from './ScorecardModal';

const ExamAttemptsTable = () => {
  const [attempts, setAttempts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [examOptions, setExamOptions] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState('');

  const [filters, setFilters] = useState({
    limit: 30,
    offset: 0,
    currentPage: 1,
    type: 'all',
    search: ''
  });

  const fetchAttempts = async () => {
    setIsLoading(true);
    try {
      const response = await service.getExamAttempts(
        filters.limit,
        filters.offset,
        filters.type,
        filters.search,
        selectedExamId
      );
      setAttempts(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error("Failed to fetch exam attempts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAttempts();
  }, [filters.limit, filters.offset, filters.type, selectedExamId]);

  // Load Exam Options based on selected type
  useEffect(() => {
    const loadExams = async () => {
      setSelectedExamId(''); // reset
      setExamOptions([]);
      if (filters.type === 'bodmas') {
        try {
          const res = await service.getBodmasTestSeries(100, 0, 'active', '');
          setExamOptions(res.data.result || []);
        } catch (err) {
          console.error("Failed to load bodmas series:", err);
        }
      } else if (filters.type === 'mock') {
        try {
          // getmockTest expects (limit, offset, status, keyword, id)
          // We pass empty id to fetch all mock tests
          const res = await service.getmockTest(100, 0, 'active', '', '');
          setExamOptions(res.data.result || []);
        } catch (err) {
          console.error("Failed to load mock tests:", err);
        }
      }
    };
    loadExams();
  }, [filters.type]);

  // Debounced/Delayed search on search input change
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setFilters(prev => ({ ...prev, offset: 0, currentPage: 1 }));
      fetchAttempts();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [filters.search]);

  const pageChange = (page) => {
    const offset = filters.limit * (page - 1);
    setFilters(prev => ({
      ...prev,
      currentPage: page,
      offset: offset
    }));
  };

  const handleTypeChange = (e) => {
    setFilters(prev => ({
      ...prev,
      type: e.target.value,
      offset: 0,
      currentPage: 1
    }));
  };

  const handleSearchChange = (e) => {
    setFilters(prev => ({
      ...prev,
      search: e.target.value
    }));
  };

  const handleExamIdChange = (e) => {
    setSelectedExamId(e.target.value);
    setFilters(prev => ({
      ...prev,
      offset: 0,
      currentPage: 1
    }));
  };

  const openScorecard = (attempt) => {
    setSelectedAttempt(attempt);
    setIsModalOpen(true);
  };

  const exportCSV = async () => {
    try {
      setIsLoading(true);
      const response = await service.exportExamAttempts(
        filters.type,
        filters.search,
        selectedExamId
      );
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `student_attempts_${filters.type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Failed to export all attempts:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  return (
    <Fragment>
      <Col sm='12'>
        <Card>
          <CardHeader className='pb-0'>
            <Row className='align-items-center mb-3'>
              <Col md='4' sm='6'>
                <Label for='search' className='f-w-600'>Search Student</Label>
                <Input
                  id='search'
                  type='text'
                  placeholder='Search by Name or Email...'
                  value={filters.search}
                  onChange={handleSearchChange}
                />
              </Col>
              <Col md='4' sm='6'>
                <Label for='examType' className='f-w-600'>Exam Type</Label>
                <Input
                  id='examType'
                  type='select'
                  value={filters.type}
                  onChange={handleTypeChange}
                >
                  <option value='all'>All Exams</option>
                  <option value='bodmas'>Bodmas Test Series</option>
                  <option value='mock'>Mock Test</option>
                  <option value='pyq'>PYQ</option>
                  <option value='live'>Live Practice</option>
                  <option value='recorded'>Recorded Class Test</option>
                </Input>
              </Col>
              {examOptions.length > 0 && (
                <Col md='4' sm='6'>
                  <Label for='examSelect' className='f-w-600'>
                    {filters.type === 'bodmas' ? 'Select Bodmas Test Series' : 'Select Mock Test'}
                  </Label>
                  <Input
                    id='examSelect'
                    type='select'
                    value={selectedExamId}
                    onChange={handleExamIdChange}
                  >
                    <option value=''>All</option>
                    {examOptions.map(opt => (
                      <option key={opt._id} value={opt._id}>
                        {opt.title || opt.name || opt._id}
                      </option>
                    ))}
                  </Input>
                </Col>
              )}
            </Row>
            <Row className='mb-2'>
              <Col className='text-end'>
                <button
                  className='btn btn-success btn-sm'
                  onClick={exportCSV}
                  disabled={attempts.length === 0 || !filters.type || filters.type === 'all' || !selectedExamId}
                >
                  <i className='fa fa-download me-1'></i> Export to CSV
                </button>
              </Col>
            </Row>
          </CardHeader>

          <div className='table-responsive'>
            <Table hover={true} className='table-border-horizontal table-light'>
              <thead>
                <tr>
                  <th scope='col'>Sl.No</th>
                  <th scope='col'>Student Name</th>
                  <th scope='col'>Email</th>
                  <th scope='col'>Exam Title</th>
                  <th scope='col'>Exam Type</th>
                  <th scope='col'>Score</th>
                  {selectedExamId && <th scope='col'>Rank</th>}
                  <th scope='col'>Submission Date</th>
                  <th scope='col'>Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={selectedExamId ? '9' : '8'} className='text-center py-4'>
                      <div className='loader-box'>
                        <div className='loader-3'></div>
                      </div>
                      <span className='ms-2'>Loading attempts...</span>
                    </td>
                  </tr>
                ) : (
                  attempts.map((item, index) => (
                    <tr key={item._id}>
                      <th scope='row'>{filters.offset + index + 1}</th>
                      <td>{item.studentName}</td>
                      <td>{item.studentEmail}</td>
                      <td>{item.examTitle}</td>
                      <td>
                        <span className={`badge ${
                          item.examType === 'Bodmas Test' ? 'bg-primary' :
                          item.examType === 'Mock Test' ? 'bg-success' :
                          item.examType === 'PYQ' ? 'bg-info' :
                          item.examType === 'Live Practice' ? 'bg-warning text-dark' : 'bg-secondary'
                        }`}>
                          {item.examType}
                        </span>
                      </td>
                      <td>
                        <span className='font-bold text-success'>
                          {item.score}
                        </span>
                        <span className='text-muted'> / {item.maxScore}</span>
                      </td>
                      {selectedExamId && (
                        <td>
                          <span className={`badge ${
                            item.rank === 1 ? 'bg-warning text-dark' :
                            item.rank === 2 ? 'bg-light text-dark' :
                            item.rank === 3 ? 'bg-danger text-white' : 'bg-secondary'
                          }`} style={{ fontWeight: 700, fontSize: '12px' }}>
                            {item.rank}
                          </span>
                        </td>
                      )}
                      <td>{item.completedAt ? new Date(item.completedAt).toLocaleString() : new Date(item.createdAt).toLocaleString()}</td>
                      <td>
                        <button
                          className='btn btn-xs btn-primary'
                          onClick={() => openScorecard(item)}
                        >
                          <i className='fa fa-eye me-1'></i> View Scorecard
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
          {!isLoading && attempts.length === 0 && <Empty />}
        </Card>
        {!isLoading && totalCount > 0 && (
          <Pagination
            currentPage={filters.currentPage}
            totalItem={totalCount}
            itemsPerPage={filters.limit}
            showEllipsisAfter={true}
            visiblePageCount={3}
            onPageChange={pageChange}
          />
        )}
      </Col>

      {selectedAttempt && (
        <ScorecardModal
          isOpen={isModalOpen}
          toggle={toggleModal}
          attempt={selectedAttempt}
        />
      )}
    </Fragment>
  );
};

export default ExamAttemptsTable;
